import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, Space, Form, Modal, Input, message, Upload, Select, Popconfirm, Row, Col, Empty, Table, Pagination } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, BookOutlined, SearchOutlined, KeyOutlined, AppstoreOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  fetchCourses, createCourse, updateCourse, deleteCourse, uploadCourseImage,
  fetchAvailableLogins, fetchCourseAccess, grantCourseAccess, revokeCourseAccess
} from '../../../../Service/Courses_API/coursesApi';
import { SERVER_URL } from '../../../../Service/apiConfig';
import { fetchClusters } from '../../../../Service/Clusters_API/clustersApi';
import { fetchAcademicYears } from '../../../../Service/Dashboard_API/dashboardApi';
import ModulesPage from '../../Modules/ModulesContainer';

const CoursesPage = ({ userRole }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(() => {
    const saved = localStorage.getItem('selectedCourse');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    if (selectedCourse) {
      localStorage.setItem('selectedCourse', JSON.stringify(selectedCourse));
    } else {
      localStorage.removeItem('selectedCourse');
      localStorage.removeItem('selectedModule');
      localStorage.removeItem('selectedVideo');
    }
  }, [selectedCourse]);
  const [loading, setLoading] = useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [courseForm] = Form.useForm();
  const selectedFormYear = Form.useWatch('academic_year_id', courseForm);
  
  const [clusters, setClusters] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [courseImgFile, setCourseImgFile] = useState(null);
  const [courseImgLoading, setCourseImgLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Access control states
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [currentAccessCourse, setCurrentAccessCourse] = useState(null);
  const [availableLogins, setAvailableLogins] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [accessForm] = Form.useForm();
  const [editingAccessId, setEditingAccessId] = useState(null);
  const [editingAccessType, setEditingAccessType] = useState(null);
  const [accessSearchText, setAccessSearchText] = useState('');

  const loadCoursesList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (selectedYear && selectedYear !== 'all') params.academic_year_id = selectedYear;
      const data = await fetchCourses(params);
      setCourses(data);
      const clusterList = await fetchClusters();
      setClusters(clusterList);
      const yearsList = await fetchAcademicYears();
      setAcademicYears(yearsList);
    } catch (e) {
      console.warn('Failed to load metadata:', e.message);
      message.error('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedYear]);

  useEffect(() => {
    loadCoursesList();
  }, [loadCoursesList]);

  const handleSaveCourse = async (values) => {
    try {
      let finalImgName = '';
      if (fileList && fileList.length > 0) {
        if (courseImgFile) {
          setCourseImgLoading(true);
          const formData = new FormData();
          formData.append('image', courseImgFile);
          const uploadRes = await uploadCourseImage(formData);
          finalImgName = uploadRes.filename;
        } else if (editingItem && editingItem.courses_image) {
          finalImgName = editingItem.courses_image;
        }
      } else {
        finalImgName = '';
      }

      const payload = {
        ...values,
        courses_image: finalImgName
      };

      if (editingItem) {
        await updateCourse(editingItem.courses_id, payload);
        message.success('Course updated successfully.');
      } else {
        await createCourse(payload);
        message.success('Course created successfully.');
      }
      setIsCourseModalOpen(false);
      courseForm.resetFields();
      setFileList([]);
      setCourseImgFile(null);
      setEditingItem(null);
      loadCoursesList();
    } catch (e) {
      console.error(e);
      message.error(e.message || 'Failed to save course.');
    } finally {
      setCourseImgLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId, e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    try {
      await deleteCourse(courseId);
      message.success('Course deleted.');
      loadCoursesList();
    } catch (e) {
      message.error(e.message || 'Failed to delete course.');
    }
  };

  const handleManageAccess = async (course) => {
    setCurrentAccessCourse(course);
    setIsAccessModalOpen(true);
    setAccessLoading(true);
    try {
      const list = await fetchCourseAccess(course.courses_id);
      setAccessList(list);
      
      const logins = await fetchAvailableLogins();
      setAvailableLogins(logins);
    } catch (e) {
      message.error('Failed to load access list.');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleGrantAccess = async (values) => {
    setGrantLoading(true);
    try {
      await grantCourseAccess(currentAccessCourse.courses_id, {
        login_ids: values.login_ids,
        courses_access_type: values.courses_access_type
      });
      message.success('Access granted successfully.');
      accessForm.resetFields(['login_ids', 'courses_access_type']);
      // Reload list
      const list = await fetchCourseAccess(currentAccessCourse.courses_id);
      setAccessList(list);
    } catch (e) {
      message.error(e.message || 'Failed to grant access.');
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    try {
      await revokeCourseAccess(accessId);
      message.success('Access revoked.');
      // Reload list
      const list = await fetchCourseAccess(currentAccessCourse.courses_id);
      setAccessList(list);
    } catch (e) {
      message.error(e.message || 'Failed to revoke access.');
    }
  };

  const filteredCourses = courses.filter(c => {
    if ((userRole === 'STUDIO' || userRole === 'USER') && c.courses_access_type === 'LIVE') {
      return false;
    }
    if (selectedYear !== 'all' && String(c.academic_year_id) !== String(selectedYear)) {
      return false;
    }
    const queryStr = (searchText || '').toLowerCase().trim();
    if (!queryStr) return true;

    const cluster = clusters.find(cl => cl.cluster_id === c.cluster_id);
    const academicYear = academicYears.find(y => y.academic_year_id === c.academic_year_id);

    const nameMatch = (c.courses_name || '').toLowerCase().includes(queryStr);
    const descMatch = (c.courses_description || '').toLowerCase().includes(queryStr);
    const clusterMatch = cluster ? (cluster.cluster_name || '').toLowerCase().includes(queryStr) : false;
    const yearMatch = academicYear ? (academicYear.academic_year || '').toLowerCase().includes(queryStr) : false;

    return nameMatch || descMatch || clusterMatch || yearMatch;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (sortOrder === 'asc') {
      return a.courses_id - b.courses_id;
    } else {
      return b.courses_id - a.courses_id;
    }
  });

  if (selectedCourse) {
    if (window.location.pathname !== '/modules' && window.location.pathname !== '/module-videos' && window.location.pathname !== '/video-player' && window.location.pathname !== '/video-edit') {
      window.history.pushState({}, '', `/modules?courseId=${selectedCourse.courses_id}`);
    }
    return (
      <ModulesPage
        course={selectedCourse}
        userRole={userRole}
        onBack={() => {
          setSelectedCourse(null);
          window.history.pushState({}, '', '/courses');
        }}
      />
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <style>{`
        .course-card-adaptive {
          background: #ffffff !important;
          border: 1px solid var(--border-color, #e2e8f0) !important;
          border-radius: 16px;
          overflow: hidden;
          min-height: 370px;
          height: auto;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .course-card-adaptive:hover {
          transform: translateY(-4px);
          border-color: rgba(59, 130, 246, 0.4) !important;
          box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.1) !important;
        }
        [data-theme='dark'] .course-card-adaptive {
          background: linear-gradient(145deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8)) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }
        [data-theme='dark'] .course-card-adaptive:hover {
          box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.5) !important;
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Courses</h2>
        </div>
        {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingItem(null);
              courseForm.resetFields();
              setFileList([]);
              setCourseImgFile(null);
              setIsCourseModalOpen(true);
            }}
            style={{ borderRadius: 6 }}
          >
            Create Course
          </Button>
        )}
      </div>

      {/* Search Filter Bar */}
      <div style={{ marginBottom: 24 }}>
        <Space wrap size="middle">
          <Input
            placeholder="Search courses by name, description, cluster or year..."
            prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              color: 'var(--text-color)',
              width: 350
            }}
          />
          <Select showSearch optionFilterProp="children"
            value={selectedYear}
            onChange={(val) => {
              setSelectedYear(val);
              setCurrentPage(1);
            }}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'All Academic Years' },
              ...academicYears.filter(ay => ay.status === 'Active' || ay.status === 'ACTIVE').map(ay => ({ value: String(ay.academic_year_id), label: ay.academic_year }))
            ]}
          />
          <Select showSearch optionFilterProp="children"
            value={sortOrder}
            onChange={(val) => {
              setSortOrder(val);
              setCurrentPage(1);
            }}
            style={{ width: 160 }}
            options={[
              { value: 'desc', label: 'Descending (Newest)' },
              { value: 'asc', label: 'Ascending (Oldest)' }
            ]}
          />
        </Space>
      </div>

      {/* Courses Cards Grid */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <span className="spinner-border text-primary">Loading courses...</span>
        </div>
      ) : sortedCourses.length > 0 ? (
        <React.Fragment>
          <Row gutter={[24, 24]}>
            {sortedCourses
              .filter((c) => userRole === 'ADMIN' || c.courses_access_type !== 'BLOCK')
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map(course => {
                const cluster = clusters.find(c => c.cluster_id === course.cluster_id);
                const academicYear = academicYears.find(y => y.academic_year_id === course.academic_year_id);

                return (
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} key={course.courses_id}>
                    <Card
                      className="course-card-adaptive"
                      hoverable
                      onClick={() => setSelectedCourse(course)}
                      bodyStyle={{
                        padding: 18,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                      cover={
                        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {course.courses_image ? (
                            <img
                              src={`${SERVER_URL}/${course.courses_directory || 'Uploads'}/${course.courses_image}`}
                              alt={course.courses_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}>
                              <BookOutlined style={{ fontSize: 42, color: 'rgba(255,255,255,0.2)' }} />
                            </div>
                          )}

                          {/* Course Stats Overlay Badge */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 10,
                              right: 12,
                              zIndex: 2,
                              background: 'rgba(0, 0, 0, 0.75)',
                              backdropFilter: 'blur(4px)',
                              padding: '3px 9px',
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              fontSize: 12,
                              color: '#fff',
                              fontWeight: 500
                            }}
                          >
                            <AppstoreOutlined style={{ color: '#38bdf8' }} />
                            <span>{course.module_count || 0} Modules • {course.video_count || 0} Videos</span>
                          </div>
                        </div>
                      }
                    >
                      <div style={{ marginBottom: 12 }}>
                        <h3 style={{ color: 'var(--text-color, #1e293b)', fontSize: 16, marginBottom: 6, fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.4, letterSpacing: '-0.01em' }}>
                          {course.courses_name}
                        </h3>
                        {course.courses_description && (
                          <p style={{ color: 'var(--text-secondary, #64748b)', fontSize: 13, minHeight: 20, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 6, lineHeight: 1.5 }}>
                            {course.courses_description}
                          </p>
                        )}
                        {cluster && cluster.status === 'Active' && (
                          <Tag
                            style={{
                              borderRadius: 6,
                              border: '1px solid rgba(147, 51, 234, 0.3)',
                              background: 'rgba(147, 51, 234, 0.12)',
                              color: '#9333ea',
                              fontWeight: 500,
                              margin: '4px 0 6px 0',
                              padding: '2px 8px'
                            }}
                          >
                            {cluster.cluster_name} {cluster.academic_year ? `(${cluster.academic_year})` : ''}
                          </Tag>
                        )}

                        {/* Detailed Stats Row */}
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <Tag icon={<ClockCircleOutlined />} style={{ margin: 0, borderRadius: 6, border: '1px solid rgba(6, 182, 212, 0.3)', background: 'rgba(6, 182, 212, 0.12)', color: '#0891b2', fontSize: 11, padding: '2px 8px' }}>
                            Duration: {course.total_duration || '00:00:00'}
                          </Tag>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--border-color, rgba(0, 0, 0, 0.06))' }}>
                        <div>
                          {academicYear && (
                            <Tag style={{ borderRadius: 6, border: '1px solid rgba(16, 185, 129, 0.3)', background: 'rgba(16, 185, 129, 0.12)', color: '#059669', margin: 0, padding: '2px 8px' }}>
                              {academicYear.academic_year}
                            </Tag>
                          )}
                        </div>
                        
                        {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                          <Space size={6}>
                            <Button
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingItem(course);
                                courseForm.setFieldsValue(course);
                                setFileList(course.courses_image ? [{
                                  uid: '-1',
                                  name: course.courses_image,
                                  status: 'done',
                                  url: `${SERVER_URL}/${course.courses_directory || 'Uploads'}/${course.courses_image}`
                                }] : []);
                                setCourseImgFile(null);
                                setIsCourseModalOpen(true);
                              }}
                              style={{ borderRadius: 6, display: 'flex', alignItems: 'center', background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)', color: '#818cf8' }}
                            />
                            <Button
                              icon={<KeyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageAccess(course);
                              }}
                              style={{ borderRadius: 6, display: 'flex', alignItems: 'center', background: 'rgba(250, 173, 20, 0.1)', border: '1px solid rgba(250, 173, 20, 0.3)', color: '#fbbf24' }}
                            />
                            <Popconfirm
                              title="Delete this course?"
                              onConfirm={(e) => handleDeleteCourse(course.courses_id, e)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                style={{ borderRadius: 6, display: 'flex', alignItems: 'center' }}
                              />
                            </Popconfirm>
                          </Space>
                        )}
                      </div>
                    </Card>
                  </Col>
                );
              })}
          </Row>

          {sortedCourses.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, flexWrap: 'wrap', gap: 12 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, sortedCourses.length)} - {Math.min(currentPage * pageSize, sortedCourses.length)} of {sortedCourses.length} courses
              </span>
              {sortedCourses.length > pageSize && (
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={sortedCourses.length}
                  onChange={page => setCurrentPage(page)}
                  showSizeChanger={false}
                />
              )}
            </div>
          )}
        </React.Fragment>
      ) : (
        <Empty description={<span style={{ color: 'var(--text-secondary)' }}>No courses found matching search query.</span>} style={{ margin: '40px auto' }} />
      )}

      {/* Course Creation/Editing Modal */}
      <Modal
        title={editingItem ? 'Edit Course' : 'Create Course'}
        open={isCourseModalOpen}
        onCancel={() => {
          setIsCourseModalOpen(false);
          setFileList([]);
          setCourseImgFile(null);
          setEditingItem(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={courseForm} layout="vertical" onFinish={handleSaveCourse}>
          <Form.Item name="courses_name" label="Course Name" rules={[{ required: true, message: 'Please enter course name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="courses_description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          
          <Form.Item name="academic_year_id" label="Academic Year">
            <Select showSearch optionFilterProp="children" placeholder="Choose year" allowClear>
              {academicYears.filter(y => y.status === 'Active' || y.status === 'ACTIVE' || !y.status).map(y => <Select.Option key={y.academic_year_id} value={y.academic_year_id}>{y.academic_year}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="cluster_id" label="Linked Cluster">
            <Select showSearch optionFilterProp="children" placeholder="Choose cluster" allowClear>
              {clusters
                .filter(cl => cl.status === 'Active')
                .filter(cl => !selectedFormYear || cl.academic_year_id === selectedFormYear)
                .map(cl => (
                <Select.Option key={cl.cluster_id} value={cl.cluster_id}>
                  {cl.cluster_name} {cl.academic_year ? `(${cl.academic_year})` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Upload Course Image">
            <Upload
              beforeUpload={(file) => {
                setCourseImgFile(file);
                return false;
              }}
              maxCount={1}
              listType="picture"
              onRemove={() => {
                setCourseImgFile(null);
                setFileList([]);
              }}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
            >
              <Button icon={<UploadOutlined />}>Select Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setIsCourseModalOpen(false);
                setFileList([]);
                setCourseImgFile(null);
                setEditingItem(null);
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={courseImgLoading}>
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Access Control Modal */}
      <Modal
        title={`Manage Access - ${currentAccessCourse?.courses_name}`}
        open={isAccessModalOpen}
        onCancel={() => {
          setIsAccessModalOpen(false);
          setCurrentAccessCourse(null);
          setEditingAccessId(null);
          setAccessSearchText('');
          accessForm.resetFields();
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 12 }}>Grant New Access</h4>
          <Form form={accessForm} layout="inline" onFinish={handleGrantAccess} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
            <Form.Item name="login_ids" rules={[{ required: true, message: 'Select user(s)' }]} style={{ minWidth: 240, margin: 0 }}>
              <Select showSearch
                mode="multiple"
                placeholder="Select Studio or Student account"
                optionFilterProp="label"
                style={{ width: '100%' }}
                options={availableLogins
                  .filter(l => !accessList.some(a => a.login_id === l.login_id))
                  .map(l => ({
                    value: l.login_id,
                    label: `${l.login_name} (${l.login_username} - ${l.login_role})`
                  }))}
              />
            </Form.Item>
            
            <Form.Item name="courses_access_type" rules={[{ required: true, message: 'Select type' }]} style={{ minWidth: 140, margin: 0 }}>
              <Select showSearch optionFilterProp="children" placeholder="Access Type">
                <Select.Option value="LIVE">LIVE</Select.Option>
                <Select.Option value="VOD">VOD</Select.Option>
                <Select.Option value="LIVE/VOD">LIVE/VOD</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item style={{ margin: 0 }}>
              <Button type="primary" htmlType="submit" loading={grantLoading}>
                Grant Access
              </Button>
            </Form.Item>
          </Form>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Current Access List</h4>
            <Input 
              placeholder="Search by name, username or role..." 
              value={accessSearchText}
              onChange={(e) => setAccessSearchText(e.target.value)}
              prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
              style={{ width: 280 }}
              allowClear
            />
          </div>
          <Table
            dataSource={accessList.filter(item => {
              const term = accessSearchText.toLowerCase();
              return (item.login_name || '').toLowerCase().includes(term) ||
                     (item.login_username || '').toLowerCase().includes(term) ||
                     (item.login_role || '').toLowerCase().includes(term);
            })}
            rowKey="courses_access_id"
            loading={accessLoading}
            size="small"
            pagination={{ pageSize: 20 }}
            scroll={{ x: 'max-content', y: 400 }}
            columns={[
              { title: 'Name', dataIndex: 'login_name', key: 'login_name' },
              { title: 'Username', dataIndex: 'login_username', key: 'login_username' },
              {
                title: 'Role',
                dataIndex: 'login_role',
                key: 'login_role',
                render: (role) => <Tag color={role === 'STUDIO' ? 'orange' : 'blue'}>{role}</Tag>
              },
              {
                title: 'Access Type',
                dataIndex: 'courses_access_type',
                key: 'courses_access_type',
                render: (type, record) => {
                  const isEditing = record.courses_access_id === editingAccessId;
                  if (isEditing) {
                    return (
                      <Select showSearch optionFilterProp="children"
                        value={editingAccessType}
                        size="small"
                        style={{ width: 120 }}
                        onChange={(val) => setEditingAccessType(val)}
                      >
                        <Select.Option value="LIVE">LIVE</Select.Option>
                        <Select.Option value="VOD">VOD</Select.Option>
                        <Select.Option value="LIVE/VOD">LIVE/VOD</Select.Option>
                      </Select>
                    );
                  }
                  return <Tag color="green">{type}</Tag>;
                }
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 140,
                render: (_, record) => {
                  const isEditing = record.courses_access_id === editingAccessId;
                  if (isEditing) {
                    return (
                      <Space size={8}>
                        <Button
                          size="small"
                          type="link"
                          onClick={async () => {
                            try {
                              await grantCourseAccess(currentAccessCourse.courses_id, {
                                login_id: record.login_id,
                                courses_access_type: editingAccessType
                              });
                              message.success('Access updated successfully.');
                              setEditingAccessId(null);
                              const list = await fetchCourseAccess(currentAccessCourse.courses_id);
                              setAccessList(list);
                            } catch (e) {
                              message.error('Failed to update access.');
                            }
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => setEditingAccessId(null)}
                        >
                          Cancel
                        </Button>
                      </Space>
                    );
                  }
                  return (
                    <Space size={8}>
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingAccessId(record.courses_access_id);
                          setEditingAccessType(record.courses_access_type);
                        }}
                      />
                      <Popconfirm
                        title="Revoke access for this user?"
                        onConfirm={() => handleRevokeAccess(record.courses_access_id)}
                      >
                        <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  );
                }
              }
            ]}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CoursesPage;
